import axiosInstance from '../lib/axios';
import { 
  TimesheetEntry, 
  StartTimerRequest, 
  StopTimerRequest
} from '../types/models';

class TimesheetService {
  private baseUrl = '/timesheet';

  // Start a timer for a task
  async startTimer(taskId: string, notes?: string): Promise<TimesheetEntry> {
    try {
      console.log(`[TimesheetService] Starting timer for task: ${taskId}`);
      const response = await axiosInstance.post<TimesheetEntry>(
        `${this.baseUrl}/start`,
        { taskId, notes } as StartTimerRequest
      );
      console.log(`[TimesheetService] Timer started:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error(`[TimesheetService] Error starting timer:`, error);
      console.error(`[TimesheetService] Error details:`, error.response?.data);
      throw error;
    }
  }

  // Stop a running timer
  async stopTimer(entryId: string): Promise<TimesheetEntry> {
    try {
      console.log(`[TimesheetService] Stopping timer for entry: ${entryId}`);
      const response = await axiosInstance.post<TimesheetEntry>(
        `${this.baseUrl}/stop`,
        { entryId } as StopTimerRequest
      );
      console.log(`[TimesheetService] Timer stopped:`, response.data);
      return response.data;
    } catch (error: any) {
      console.error(`[TimesheetService] Error stopping timer:`, error);
      console.error(`[TimesheetService] Error details:`, error.response?.data);
      throw error;
    }
  }

  // Get all timesheet entries for a specific task
  async getTimesheetEntriesByTask(taskId: string): Promise<TimesheetEntry[]> {
    try {
      console.log(`[TimesheetService] Fetching timesheet entries for task: ${taskId}`);
      const response = await axiosInstance.get(`/tasks/${taskId}/timesheet`);
      console.log(`[TimesheetService] Raw API response:`, response.data);
      
      let entries: any[] = [];
      
      // Handle different response formats
      if (Array.isArray(response.data)) {
        entries = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        entries = response.data.data;
      } else if (response.data && Array.isArray(response.data.entries)) {
        entries = response.data.entries;
      } else {
        console.warn('[TimesheetService] Unexpected response format:', response.data);
        return [];
      }
      
      // Normalize: map timeSpentMs -> duration (seconds), task -> taskId, member -> employeeId
      const normalized: TimesheetEntry[] = entries.map((e: any) => ({
        _id: e._id,
        taskId: e.taskId || e.task || '',
        employeeId: e.employeeId || e.member || '',
        startTime: e.startTime,
        endTime: e.endTime,
        notes: e.notes,
        duration: typeof e.duration === 'number' ? e.duration : (typeof e.timeSpentMs === 'number' ? Math.floor(e.timeSpentMs / 1000) : undefined),
        timeSpentMs: typeof e.timeSpentMs === 'number' ? e.timeSpentMs : undefined,
        employee: e.employee || e.member, // Handle both employee and member objects
        createdAt: e.createdAt,
        updatedAt: e.updatedAt,
      }));

      // Log each entry's timestamps for debugging
      normalized.forEach((entry, index) => {
        console.log(`[TimesheetService] Entry ${index}:`, {
          id: entry._id,
          startTime: entry.startTime,
          endTime: entry.endTime,
          durationSeconds: entry.duration,
          timeSpentMs: entry.timeSpentMs,
          notes: entry.notes
        });
      });
      
      return normalized;
    } catch (error: any) {
      console.error(`[TimesheetService] Error fetching timesheet entries for task ${taskId}:`, error);
      console.error(`[TimesheetService] Error details:`, error.response?.data);
      throw error;
    }
  }

  // Update timesheet entry
  async updateTimesheetEntry(entryId: string, updates: {
    startTime?: string;
    endTime?: string;
    memberId?: string;
    notes?: string;
  }): Promise<TimesheetEntry> {
    try {
      console.log(`[TimesheetService] Updating timesheet entry: ${entryId}`, updates);
      const response = await axiosInstance.patch<any>(
        `${this.baseUrl}/${entryId}`,
        updates
      );
      console.log(`[TimesheetService] Entry updated:`, response.data);
      
      // Normalize the response to match TimesheetEntry interface
      const entry = response.data;
      const normalized: TimesheetEntry = {
        _id: entry._id,
        taskId: entry.taskId || entry.task || '',
        employeeId: entry.employeeId || entry.member || '',
        startTime: entry.startTime,
        endTime: entry.endTime,
        notes: entry.notes,
        duration: typeof entry.duration === 'number' ? entry.duration : (typeof entry.timeSpentMs === 'number' ? Math.floor(entry.timeSpentMs / 1000) : undefined),
        timeSpentMs: typeof entry.timeSpentMs === 'number' ? entry.timeSpentMs : undefined,
        employee: entry.employee || entry.member || entry.memberDetails,
        createdAt: entry.createdAt,
        updatedAt: entry.updatedAt,
      };
      
      console.log(`[TimesheetService] Normalized updated entry:`, normalized);
      return normalized;
    } catch (error: any) {
      console.error(`[TimesheetService] Error updating entry:`, error);
      console.error(`[TimesheetService] Error details:`, error.response?.data);
      throw error;
    }
  }

  // Delete a timesheet entry
  async deleteTimesheetEntry(entryId: string): Promise<void> {
    try {
      console.log(`[TimesheetService] Deleting timesheet entry: ${entryId}`);
      await axiosInstance.delete(`${this.baseUrl}/${entryId}`);
      console.log(`[TimesheetService] Entry deleted`);
    } catch (error: any) {
      console.error(`[TimesheetService] Error deleting entry:`, error);
      console.error(`[TimesheetService] Error details:`, error.response?.data);
      throw error;
    }
  }
}

export const timesheetService = new TimesheetService();
export default timesheetService;

